import React, { useState, useRef, useEffect } from 'react';
import { Settings } from 'lucide-react';

// Add sectionTitle to the props
const UserPromptSelector = ({ sectionKey, sectionTitle, onPromptChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const dropdownRef = useRef(null);

  // Load current prompt when component mounts
  useEffect(() => {
    loadCurrentPrompt();
  }, [sectionKey]);

  const loadCurrentPrompt = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/user/section-prompts?section=${sectionKey}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentPrompt(data.prompt || '');
      }
    } catch (error) {
      console.error('Error loading user prompt:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const savePrompt = async (prompt) => {
    setIsSaving(true);
    try {
      const response = await fetch('/api/user/section-prompts', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          section_key: sectionKey,
          prompt: prompt
        })
      });

      if (response.ok) {
        setCurrentPrompt(prompt);
        if (onPromptChange) {
          onPromptChange(sectionKey, prompt);
        }
        setShowModal(false);
      } else {
        throw new Error('Failed to save prompt');
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Failed to save prompt. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditPrompt = () => {
    setShowModal(true);
    setIsOpen(false);
  };

  const handleClearPrompt = async () => {
    await savePrompt('');
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Updated function - now it's only used as a fallback
  const formatSectionKey = (key) => {
    const sectionMap = {
      'hpi': 'History of Present Illness',
      'ros': 'Review of Symptoms',
      'assessment_plan': 'Assessment & Plan',
      'physical_exam': 'Physical Exam'
    };
    return sectionMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Use sectionTitle if provided, otherwise fall back to formatting the key
  const displayTitle = sectionTitle || formatSectionKey(sectionKey);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className={`px-2 py-1 text-sm rounded-md border transition-colors duration-200 flex items-center gap-1 ${
            currentPrompt 
              ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' 
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
          disabled={isLoading}
        >
          {/*<Settings className="w-3 h-3" />*/}
          {isLoading ? 'Loading...' : 'Custom Instruction'}
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEditPrompt();
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              {currentPrompt ? 'Edit Prompt' : 'Add Prompt'}
            </button>
            {currentPrompt && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearPrompt();
                }}
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                Clear Prompt
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal for editing prompt */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          onClick={(e) => {
            e.stopPropagation();
            setShowModal(false);
            setCurrentPrompt(currentPrompt); // Keep the current prompt instead of reloading
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium mb-4">
              Custom Prompt for {displayTitle}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Instructions
              </label>
              <textarea
                value={currentPrompt}
                onChange={(e) => setCurrentPrompt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={6}
                placeholder="Enter additional instructions for this section. This will be added to the AI's prompt when processing this section..."
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className="text-sm text-gray-600 mb-4">
              <p><strong>Examples:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>"Always include vital signs when mentioned"</li>
                <li>"Use bullet points for all symptoms"</li>
                <li>"Include medication dosages and frequencies"</li>
                <li>"Focus on chronic conditions and their management"</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowModal(false);
                  setCurrentPrompt(currentPrompt); // Keep the current prompt instead of reloading
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  savePrompt(currentPrompt);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Prompt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserPromptSelector;